<script setup lang="ts">
/**
 * App-wide payment banner (DashboardLayout). Shown on every page except /billing, which has its own
 * detailed notice. Urgency here is real: the grace deadline comes from the API (grace_ends_at).
 */
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AlertCircle, Clock, CreditCard, Loader2, Receipt } from 'lucide-vue-next'
import api from '@/lib/api'
import { usePlan } from '@/composables/usePlan'
import { track } from '@/lib/analytics'

const props = defineProps<{ canManageBilling: boolean }>()

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { planInfo } = usePlan()
const portalLoading = ref(false)

const status = computed(() => planInfo.value?.status)
const isPastDue = computed(() => status.value === 'past_due')
const isIncomplete = computed(() => status.value === 'incomplete')
const visible = computed(() => (isPastDue.value || isIncomplete.value) && route.path !== '/billing')

const graceEnds = computed(() => {
  const raw = planInfo.value?.grace_ends_at
  if (!raw) return null
  const date = new Date(raw)
  return isNaN(date.getTime()) ? null : date
})
const graceLabel = computed(() => graceEnds.value?.toLocaleDateString(locale.value, { month: 'long', day: 'numeric' }) || null)
const graceExpired = computed(() => isPastDue.value && (planInfo.value?.plan === 'free' || (!!graceEnds.value && graceEnds.value.getTime() < Date.now())))
const invoiceUrl = computed(() => {
  const url = planInfo.value?.latest_invoice_url
  return url && /^https:\/\//.test(url) ? url : null
})

const message = computed(() => {
  if (isIncomplete.value) return t('payBanner.incomplete')
  if (graceExpired.value) return t('payBanner.expired')
  if (graceLabel.value) return t('payBanner.pastDue', { date: graceLabel.value })
  return t('payBanner.pastDueNoDate')
})

function onPayInvoice() {
  track('billing_cta_clicked', { source: 'past_due_banner', feature: 'pay_invoice', status: status.value })
}

async function updateCard() {
  track('billing_cta_clicked', { source: 'past_due_banner', feature: 'update_card', status: status.value })
  portalLoading.value = true
  try {
    const data = await api.post<{ url: string }>('/api/billing/portal', {})
    if (data.url) {
      window.location.href = data.url
      return
    }
  } catch {
    /* fall through to the billing page */
  } finally {
    portalLoading.value = false
  }
  void router.push('/billing')
}
</script>

<template>
  <div
    v-if="visible"
    :role="isPastDue ? 'alert' : 'status'"
    class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2.5 text-sm sm:px-6"
    :class="isPastDue ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'"
  >
    <span class="flex min-w-0 flex-1 items-start gap-2">
      <AlertCircle v-if="isPastDue" class="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
      <Clock v-else class="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <span>{{ message }}</span>
    </span>
    <span v-if="props.canManageBilling" class="flex flex-wrap gap-2">
      <a
        v-if="invoiceUrl"
        :href="invoiceUrl"
        target="_blank"
        rel="noopener"
        class="inline-flex h-8 items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-semibold text-white"
        :class="isPastDue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-900 hover:bg-amber-800'"
        @click="onPayInvoice"
      ><Receipt class="h-3.5 w-3.5" />{{ isPastDue ? t('payBanner.payInvoice') : t('payBanner.completePayment') }}</a>
      <button
        v-if="isPastDue && planInfo?.has_billing_account !== false"
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-[10px] border bg-white px-3 text-[13px] font-semibold disabled:opacity-60"
        :class="isPastDue ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-amber-200 text-amber-800 hover:bg-amber-100'"
        :disabled="portalLoading"
        @click="updateCard"
      ><Loader2 v-if="portalLoading" class="h-3.5 w-3.5 animate-spin" /><CreditCard v-else class="h-3.5 w-3.5" />{{ t('payBanner.updateCard') }}</button>
      <router-link v-if="!invoiceUrl && !isPastDue" to="/billing" class="inline-flex h-8 items-center rounded-[10px] border border-amber-200 bg-white px-3 text-[13px] font-semibold text-amber-800 hover:bg-amber-100">{{ t('payBanner.details') }}</router-link>
    </span>
  </div>
</template>
